<script lang="ts">
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./AlgoliaSearchbar.translations.json";
	import AlgoliaResults from "./AlgoliaResults.svelte";

	const translate = getTranslate(translations);

	export let isOpen = false;
	let query = "";

	$: hasResultsPanel = query.trim().length >= 1;
	let topHitName = "";
	let inputRef: HTMLInputElement;
	let containerRef: HTMLElement;

	$: ghostSuffix = (() => {
		if (!topHitName || !query) {
			return "";
		}
		if (topHitName.toLowerCase().startsWith(query.toLowerCase()) && topHitName.length > query.length) {
			return topHitName.slice(query.length);
		}
		return "";
	})();

	function open(): void {
		isOpen = true;
		setTimeout(() => {
			inputRef.focus();
		}, 30);
	}

	function close(): void {
		isOpen = false;
		query = "";
	}

	function acceptGhost(): void {
		query = topHitName;
		setTimeout(() => {
			inputRef.setSelectionRange(query.length, query.length);
		}, 0);
	}

	function handleInputKeydown(e: KeyboardEvent): void {
		if (e.key === "Escape") {
			close();
			return;
		}
		if (ghostSuffix && e.key === "Tab") {
			e.preventDefault();
			acceptGhost();
			return;
		}
		if (ghostSuffix && e.key === "ArrowRight" && inputRef.selectionStart === query.length) {
			e.preventDefault();
			acceptGhost();
		}
	}

	function onWindowPointerDown(e: PointerEvent): void {
		if (isOpen && !e.composedPath().includes(containerRef)) {
			close();
		}
	}

	function onWindowKeydown(e: KeyboardEvent): void {
		if (e.key === "Escape" && isOpen) {
			close();
		}
	}
</script>

<svelte:window on:pointerdown={onWindowPointerDown} on:keydown={onWindowKeydown} />

<div class="relative w-12 sm:w-auto">
	<div
		bind:this={containerRef}
		class={isOpen
			? "searchbar-open max-w-[calc(100vw-2rem)] max-sm:fixed max-sm:left-1/2 max-sm:top-10 max-sm:w-[calc(100vw-2rem)] max-sm:-translate-x-1/2 max-sm:-translate-y-1/2 sm:relative sm:w-[32rem]"
			: "relative w-12 sm:w-[10.25rem]"}
	>
		{#if !isOpen}
			<button
				type="button"
				class="focus-visible:ring-lttertiary flex h-12 w-full items-center justify-center gap-3 rounded-3xl bg-[#FFFFFF40] px-0 py-2 text-white transition-colors hover:bg-[#FFFFFF50] focus-visible:outline-none focus-visible:ring-2 sm:justify-between sm:px-5"
				on:click={open}
				aria-label={translate("search", {}, true)}
			>
				<span class="hidden text-sm font-normal tracking-wide sm:inline">{translate("search")}</span>
				<svg class="size-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
					/>
				</svg>
			</button>
		{:else}
			<div
				class="flex h-12 w-full items-center gap-2 bg-[#FFFFFF40] px-5 py-2 {hasResultsPanel
					? 'rounded-t-[24px]'
					: 'rounded-[24px]'}"
			>
				<div class="relative min-w-0 flex-1 overflow-hidden">
					{#if ghostSuffix}
						<div
							aria-hidden="true"
							class="pointer-events-none absolute inset-y-0 left-0 flex items-center whitespace-pre text-sm font-semibold"
						>
							<span class="invisible">{query}</span><span class="text-[#AFAFAF]">{ghostSuffix}</span>
						</div>
					{/if}
					<input
						bind:this={inputRef}
						bind:value={query}
						type="search"
						placeholder={translate("searchPlaceholder", {}, true)}
						class="placeholder-ltsecondary-100 w-full bg-transparent text-sm font-semibold text-white caret-[#5B47FF] outline-none placeholder:font-normal [&::-webkit-search-cancel-button]:hidden"
						on:keydown={handleInputKeydown}
					/>
				</div>
				<svg
					class="size-5 flex-shrink-0 text-white"
					fill="none"
					viewBox="0 0 24 24"
					stroke="currentColor"
					aria-hidden="true"
				>
					<path
						stroke-linecap="round"
						stroke-linejoin="round"
						stroke-width="2"
						d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
					/>
				</svg>
			</div>

			<div
				class="bg-ltprimary-150 absolute left-0 top-full z-50 max-h-[calc(100dvh-5rem)] w-full overflow-y-auto overscroll-contain rounded-b-3xl shadow-[0_32px_32px_-5px_rgba(0,0,0,0.25)]"
				class:hidden={!hasResultsPanel}
			>
				<div class="bg-[#FFFFFF40]">
					<AlgoliaResults {query} bind:topHitName />
				</div>
			</div>
		{/if}
	</div>
</div>

<style>
	/* One-directional: grows on open, closes instantly. */
	.searchbar-open {
		animation: searchbar-grow-mobile 300ms ease-out;
	}

	@keyframes searchbar-grow-mobile {
		from {
			width: 3rem;
		}
	}

	@keyframes searchbar-grow {
		from {
			width: 164px;
		}
	}

	@media (min-width: 640px) {
		.searchbar-open {
			animation-name: searchbar-grow;
		}
	}
</style>
